import axios, { type InternalAxiosRequestConfig } from 'axios';
import { clearStoredAuthSession, USER_STORAGE_KEY } from '@/lib/authSession';
import { trackApiFailure, trackApiSuccessEvent } from '@/lib/analytics';
import { syncSubscriptionGateFromApiError } from '@/lib/subscriptionGate';
import { syncFounderAgreementGateFromApiError } from '@/lib/founderAgreementGate';
import { isDemoModeEnabled } from '@/demo/config';
import { createDemoAxiosAdapter } from '@/demo/mockApi';

// Clean up the API URL by removing any trailing characters
const cleanApiUrl = import.meta.env.VITE_API_URL?.replace(/[^a-zA-Z0-9:/._-]/g, '') || '';

const api = axios.create({
  baseURL: cleanApiUrl,
  ...(isDemoModeEnabled() ? { adapter: createDemoAxiosAdapter() } : {}),
});

let isHandlingAuthLogout = false;

type AnalyticsRequestConfig = InternalAxiosRequestConfig & {
  analyticsStartedAt?: number;
};

const withAnalyticsConfig = (
  config: InternalAxiosRequestConfig | undefined
): AnalyticsRequestConfig | undefined => config as AnalyticsRequestConfig | undefined;

const getAnalyticsDuration = (config: AnalyticsRequestConfig | undefined): number | undefined => {
  if (!config?.analyticsStartedAt) {
    return undefined;
  }

  return performance.now() - config.analyticsStartedAt;
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const data = error.response?.data;
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    if (typeof record.message === 'string') {
      return record.message;
    }
    if (typeof record.error === 'string') {
      return record.error;
    }
  }

  return error.message;
};

const readStoredToken = (): string | null => {
  try {
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    if (!userJson) {
      return null;
    }
    const user = JSON.parse(userJson) as { token?: unknown };
    if (typeof user.token !== 'string' || !user.token) {
      return null;
    }
    return user.token;
  } catch {
    return null;
  }
};

const isConsentEndpointRequest = (url?: string): boolean =>
  Boolean(url?.includes('/auth/consent'));

const isAuthenticatedAuthEndpointRequest = (url?: string): boolean =>
  Boolean(
    url?.includes('/auth/admin/')
    || url?.includes('/auth/ai-meal-plan-options')
    || url?.includes('/auth/pre-provisioned-roles')
    || url?.includes('/auth/change-password')
    || url?.includes('/auth/subscription')
    || url?.includes('/auth/founder-agreement')
    || isConsentEndpointRequest(url)
  );

const isAuthEndpointRequest = (url?: string): boolean =>
  Boolean(url?.includes('/auth/') && !isAuthenticatedAuthEndpointRequest(url));

const isPublicUnauthenticatedEndpointRequest = (url?: string): boolean =>
  isAuthEndpointRequest(url)
  || Boolean(url?.includes('/questionnaires/share/'))
  || Boolean(url?.includes('/appointments-public/'));

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const isStoredTokenExpiredOrMalformed = (token: string | null): boolean => {
  if (!token) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return true;
  }

  const exp = Number(payload.exp);
  return Number.isFinite(exp) ? exp * 1000 <= Date.now() : true;
};

const isGenericAuthorizerForbidden = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) {
    return false;
  }

  const data = error.response.data;
  if (typeof data === 'string') {
    return data.trim() === 'Forbidden';
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  const body = data as Record<string, unknown>;
  return body.message === 'Forbidden'
    && body.error === undefined
    && body.code === undefined
    && body.details === undefined
    && body.consent === undefined;
};

const readRequestReturnPath = (): string => {
  if (typeof window === 'undefined') {
    return '/dashboard';
  }

  const { pathname, search } = window.location;
  if (pathname.startsWith('/auth/subscription-required')) {
    return '/dashboard';
  }

  return `${pathname}${search}`;
};

const logoutAfterAuthFailureResponse = () => {
  if (isHandlingAuthLogout || typeof window === 'undefined') {
    return;
  }

  if (!readStoredToken()) {
    return;
  }

  isHandlingAuthLogout = true;
  clearStoredAuthSession();

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

const shouldLogoutAfterAuthError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  if (isPublicUnauthenticatedEndpointRequest(error.config?.url)) {
    return false;
  }

  const token = readStoredToken();
  if (!token) {
    return false;
  }

  if (error.response?.status === 401) {
    const code = error.response.data?.code;
    // A rejected login method does not invalidate an authenticated session.
    if (code === 'AUTH_PROVIDER_MISMATCH' || code === 'AUTH_PROVIDER_REVIEW_REQUIRED') {
      return false;
    }
    return true;
  }

  if (isGenericAuthorizerForbidden(error)) {
    return isStoredTokenExpiredOrMalformed(token);
  }

  return false;
};

// Add a request interceptor to include JWT token in all requests
api.interceptors.request.use(
  (config) => {
    const analyticsConfig = config as AnalyticsRequestConfig;
    analyticsConfig.analyticsStartedAt = performance.now();
    const token = readStoredToken();

    // Don't add Authorization header for authentication endpoints
    const isAuthEndpoint = isAuthEndpointRequest(config.url);

    // Don't add Authorization for shared questionnaire link access.
    // Submit endpoints can be used by authenticated nutritionists (on behalf of client),
    // so keep Authorization when a token exists.
    const isPublicQuestionnaire = config.url?.includes('/questionnaires/share/');
    const isPublicBooking = config.url?.includes('/appointments-public/');

    const hasExplicitAuthorizationHeader =
      Boolean(config.headers?.Authorization)
      || Boolean(config.headers?.authorization);

    if (token && !isAuthEndpoint && !isPublicQuestionnaire && !isPublicBooking && !hasExplicitAuthorizationHeader) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return analyticsConfig;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const config = withAnalyticsConfig(response.config);
    trackApiSuccessEvent({
      durationMs: getAnalyticsDuration(config),
      method: config?.method,
      status: response.status,
      url: config?.url,
    });
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      const config = withAnalyticsConfig(error.config);
      trackApiFailure({
        code: error.code,
        durationMs: getAnalyticsDuration(config),
        message: getApiErrorMessage(error),
        method: config?.method,
        status: error.response?.status,
        url: config?.url,
      });
    }

    const returnPath = readRequestReturnPath();
    const handledAsFounderAgreementBlock = syncFounderAgreementGateFromApiError(
      error,
      returnPath,
    );
    const handledAsSubscriptionBlock = syncSubscriptionGateFromApiError(
      error,
      error.config?.url,
      returnPath,
      Boolean(readStoredToken()),
    );

    if (!handledAsFounderAgreementBlock && !handledAsSubscriptionBlock && shouldLogoutAfterAuthError(error)) {
      logoutAfterAuthFailureResponse();
    }

    return Promise.reject(error);
  }
);

export default api;
